package com.schoolconnect.app.feature.homework

import com.schoolconnect.app.feature.attendance.ParentAttendanceChild
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ParentChildSelectionStore
import com.schoolconnect.app.repository.parenthomework.ParentHomeworkRepository
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import kotlinx.serialization.json.*

class ParentHomeworkViewModel(initialChildren: List<ParentAttendanceChild> = emptyList()) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private val _uiState = MutableStateFlow(ParentHomeworkUiState(children = initialChildren, isLoading = true))
    val uiState: StateFlow<ParentHomeworkUiState> = _uiState.asStateFlow()

    init {
        observeGlobalSelection()
        load()
    }

    private fun observeGlobalSelection() {
        scope.launch {
            ParentChildSelectionStore.selectedChildId.collect { childId ->
                val index = _uiState.value.children.indexOfFirst { it.id == childId }
                if (index >= 0 && index != _uiState.value.selectedChildIndex) onEvent(ParentHomeworkUiEvent.ChildSelected(index))
            }
        }
    }

    fun onEvent(event: ParentHomeworkUiEvent) {
        when (event) {
            is ParentHomeworkUiEvent.ChildSelected -> {
                if (_uiState.value.children.isNotEmpty()) {
                    _uiState.update { it.copy(selectedChildIndex = event.index.coerceIn(0, it.children.lastIndex), pageIndex = 0, totalCount = 0, open = 0, dueSoon = 0, dueToday = 0, closed = 0, errorMessage = "", isLoading = true) }
                    load()
                }
            }
            is ParentHomeworkUiEvent.SearchChanged -> { _uiState.update { it.copy(searchQuery = event.value, pageIndex = 0, totalCount = 0, isLoading = true) }; load(debounce = true) }
            is ParentHomeworkUiEvent.StatusChanged -> { _uiState.update { it.copy(status = event.value, pageIndex = 0, totalCount = 0, isLoading = true) }; load() }
            ParentHomeworkUiEvent.FiltersReset -> { _uiState.update { it.copy(searchQuery = "", status = "All", pageIndex = 0, totalCount = 0, isLoading = true) }; load() }
            is ParentHomeworkUiEvent.PageChanged -> { _uiState.update { it.copy(pageIndex = event.index.coerceAtLeast(0), isLoading = true) }; load() }
            is ParentHomeworkUiEvent.PageSizeChanged -> { _uiState.update { it.copy(pageSize = event.size.coerceIn(PaginationPolicy.PAGE_SIZE_OPTIONS.min(), PaginationPolicy.PAGE_SIZE_OPTIONS.max()), pageIndex = 0, isLoading = true) }; load() }
            is ParentHomeworkUiEvent.SortChanged -> { _uiState.update { current -> current.copy(sortField = event.field, sortAscending = if (current.sortField == event.field) !current.sortAscending else true, pageIndex = 0, isLoading = true) }; load() }
            is ParentHomeworkUiEvent.ViewRequested -> loadDetail(event.row)
            ParentHomeworkUiEvent.ViewDismissed -> _uiState.update { it.copy(viewingHomework = null, isViewingLoading = false, viewingError = "") }
            ParentHomeworkUiEvent.Refresh -> load()
        }
    }

    private fun load(debounce: Boolean = false) {
        loadJob?.cancel()
        val state = _uiState.value
        val studentId = state.selectedChild?.id
        if (studentId.isNullOrBlank()) {
            _uiState.update {
                it.copy(
                    isLoading = false,
                    rows = emptyList(),
                    totalCount = 0,
                    errorMessage = "No linked student is available for this parent context."
                )
            }
            return
        }
        loadJob = scope.launch {
            if (debounce) delay(300)
            val listResult = ParentHomeworkRepository.load(
                studentId = studentId,
                status = state.status.toApiStatus(),
                search = state.searchQuery,
                pageIndex = state.pageIndex,
                pageSize = state.pageSize,
                sortBy = state.sortField.toApiSortField(),
                order = if (state.sortAscending) "asc" else "desc",
                refreshScope = scope
            )
            val summaryResult = ParentHomeworkRepository.loadSummary(studentId)
            listResult.onSuccess { raw ->
                val allRows = raw.firstArray("homework", "items", "assignments").map { it.jsonObject.toRow() }
                val serverCount = raw.firstInt("count", "total_count", "total") ?: allRows.size
                val window = com.schoolconnect.app.feature.common.pageWindow(state.pageIndex, state.pageSize, serverCount)
                if (window.pageIndex != state.pageIndex) {
                    _uiState.update { it.copy(totalCount = serverCount, pageIndex = window.pageIndex, isLoading = false) }
                    load()
                    return@onSuccess
                }
                val summaryRaw = summaryResult.getOrNull()
                val summary = summaryRaw?.firstObject("homework_counters", "statistics", "summary", "counters") ?: summaryRaw
                _uiState.update { it.copy(rows = allRows, totalCount = serverCount, open = summary?.firstInt("open", "open_count") ?: it.open, dueSoon = summary?.firstInt("due_soon", "due_soon_count") ?: it.dueSoon, dueToday = summary?.firstInt("due_today", "due_today_count") ?: it.dueToday, closed = summary?.firstInt("closed", "closed_count") ?: it.closed, isLoading = false, errorMessage = "") }
            }.onFailure { error -> _uiState.update { it.copy(isLoading = false, errorMessage = error.message ?: "Unable to load homework.") } }
        }
    }

    private fun loadDetail(row: HomeworkRowUi) {
        _uiState.update { it.copy(viewingHomework = row, isViewingLoading = true, viewingError = "") }
        scope.launch {
            ParentHomeworkRepository.loadDetail(row.backendId.ifBlank { row.homeworkId }).onSuccess { raw -> _uiState.update { it.copy(viewingHomework = raw.toRow(row), isViewingLoading = false) } }.onFailure { error -> _uiState.update { it.copy(isViewingLoading = false, viewingError = error.message ?: "Unable to load homework details.") } }
        }
    }

    fun clear() = scope.cancel()
}

internal fun ParentHomeworkSortField.toApiSortField(): String = when (this) {
    ParentHomeworkSortField.HOMEWORK -> "title"
    ParentHomeworkSortField.CLASS -> "class"
    ParentHomeworkSortField.SUBJECT -> "subject"
    ParentHomeworkSortField.ASSIGNED_BY -> "assigned_by"
    ParentHomeworkSortField.ASSIGNED_DATE -> "created_at"
    ParentHomeworkSortField.DUE_DATE -> "due_date"
    ParentHomeworkSortField.STATUS -> "status"
}

internal fun String.toApiStatus(): String? = when {
    isBlank() || equals("All", ignoreCase = true) -> null
    else -> com.schoolconnect.app.repository.systemlookup.SystemLookupOptions.serverCode("HOMEWORK_STATUS", this)
}

private fun JsonObject.toRow(fallback: HomeworkRowUi? = null): HomeworkRowUi = HomeworkRowUi(
    backendId = firstString("id", "homework_id") ?: fallback?.backendId.orEmpty(), schoolId = firstString("school_id") ?: fallback?.schoolId.orEmpty(), classSectionId = firstString("class_section_id") ?: fallback?.classSectionId.orEmpty(), subjectId = firstString("subject_id") ?: fallback?.subjectId.orEmpty(), homeworkId = firstString("homework_id", "id") ?: fallback?.homeworkId.orEmpty(), title = firstString("title", "name") ?: fallback?.title.orEmpty(), className = firstString("class_label", "class_section") ?: listOfNotNull(firstString("class_name"), firstString("section_name")).joinToString("-").takeIf(String::isNotBlank) ?: fallback?.className ?: "—", subject = firstString("subject_name", "subject") ?: fallback?.subject ?: "—", assignedBy = firstString("assigned_by_name", "assigned_by_teacher", "teacher_name", "assigned_by") ?: fallback?.assignedBy ?: "—", assignedDate = (firstString("assigned_date", "created_at") ?: fallback?.assignedDate.orEmpty()).toDisplayDate(), dueDate = (firstString("due_date") ?: fallback?.dueDate.orEmpty()).toDisplayDate(), status = firstString("status") ?: fallback?.status ?: "—", description = firstString("description") ?: fallback?.description.orEmpty(), attachmentFileIds = firstStringList("attachment_file_ids", "file_ids") ?: fallback?.attachmentFileIds.orEmpty(), attachmentFileUrls = firstStringList("attachment_file_urls", "file_urls") ?: fallback?.attachmentFileUrls.orEmpty()
)

private fun JsonObject.firstString(vararg keys: String): String? = keys.firstNotNullOfOrNull { get(it)?.jsonPrimitive?.contentOrNull?.takeIf(String::isNotBlank) }
private fun JsonObject.firstInt(vararg keys: String): Int? = firstString(*keys)?.toIntOrNull()
private fun JsonObject.firstObject(vararg keys: String): JsonObject? = keys.firstNotNullOfOrNull { get(it)?.jsonObject }
private fun JsonObject.firstArray(vararg keys: String): JsonArray = keys.firstNotNullOfOrNull { get(it)?.jsonArray } ?: JsonArray(emptyList())
private fun JsonObject.firstStringList(vararg keys: String): List<String>? = keys.firstNotNullOfOrNull { get(it)?.jsonArray?.mapNotNull { value -> value.jsonPrimitive.contentOrNull } }
private fun String.toDisplayDate(): String = take(10).split("-").let { p -> if (p.size == 3) "${p[2]} ${listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")[p[1].toIntOrNull()?.minus(1) ?: 0]} ${p[0]}" else this }
